import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'access-donorfy',
  version: '1.0.0',
  properties: [
    {
      key: 'credentialId',
      label: 'Donorfy Credential',
      type: 'credential',
      required: true,
      typeOptions: {
        credentialType: 'donorfy',
      },
      description: 'Select your Donorfy API credential',
    },
    {
      key: 'resource',
      label: 'Resource',
      type: 'options',
      required: true,
      default: 'constituent',
      options: [
        { value: 'constituent', name: 'Constituent' },
        { value: 'transaction', name: 'Transaction' },
        { value: 'activity', name: 'Activity' },
        { value: 'list', name: 'List' },
        { value: 'system', name: 'System' },
      ],
      description: 'The Donorfy resource to interact with',
    },

    // ===== CONSTITUENT ACTIONS =====
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'getConstituent',
      options: [
        { value: 'searchConstituents', name: 'Search Constituents' },
        { value: 'getConstituent', name: 'Get Constituent' },
        { value: 'getConstituentByEmail', name: 'Get Constituent by Email' },
        { value: 'duplicateCheckPerson', name: 'Duplicate Person Check' },
        { value: 'createConstituent', name: 'Create Constituent' },
        { value: 'updateConstituent', name: 'Update Constituent' },
        { value: 'deleteConstituent', name: 'Delete Constituent' },
      ],
      displayOptions: {
        show: {
          resource: ['constituent'],
        },
      },
    },

    // ===== TRANSACTION ACTIONS =====
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'getTransaction',
      options: [
        { value: 'getTransaction', name: 'Get Transaction' },
        { value: 'createTransaction', name: 'Create Transaction' },
        { value: 'updateTransaction', name: 'Update Transaction' },
        { value: 'deleteTransaction', name: 'Delete Transaction' },
      ],
      displayOptions: {
        show: {
          resource: ['transaction'],
        },
      },
    },

    // ===== ACTIVITY ACTIONS =====
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'getActivity',
      options: [
        { value: 'getActivity', name: 'Get Activity' },
        { value: 'createActivity', name: 'Create Activity' },
        { value: 'updateActivity', name: 'Update Activity' },
      ],
      displayOptions: {
        show: {
          resource: ['activity'],
        },
      },
    },

    // ===== LIST ACTIONS =====
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'getLists',
      options: [
        { value: 'getLists', name: 'Get All Lists' },
        { value: 'getListResults', name: 'Get List Results' },
      ],
      displayOptions: {
        show: {
          resource: ['list'],
        },
      },
    },

    // ===== SYSTEM ACTIONS =====
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'getLookupTypes',
      options: [
        { value: 'getLookupTypes', name: 'Get Lookup Types' },
        { value: 'getLookups', name: 'Get Lookups' },
      ],
      displayOptions: {
        show: {
          resource: ['system'],
        },
      },
    },

    // ===== CONSTITUENT FIELDS =====
    {
      key: 'constituentId',
      label: 'Constituent ID',
      type: 'string',
      required: true,
      placeholder: 'e.g., 12345678-1234-1234-1234-123456789012',
      description: 'The GUID of the constituent',
      displayOptions: {
        show: {
          resource: ['constituent'],
          action: ['getConstituent', 'updateConstituent', 'deleteConstituent'],
        },
      },
    },
    {
      key: 'emailAddress',
      label: 'Email Address',
      type: 'string',
      required: true,
      placeholder: 'e.g., john@example.com',
      description: 'The email address to search for',
      displayOptions: {
        show: {
          resource: ['constituent'],
          action: ['getConstituentByEmail'],
        },
      },
    },
    {
      key: 'searchFor',
      label: 'Search Term',
      type: 'string',
      required: true,
      placeholder: 'e.g., John Smith',
      description: 'Keyword to search for in constituent records',
      displayOptions: {
        show: {
          resource: ['constituent'],
          action: ['searchConstituents'],
        },
      },
    },
    {
      key: 'constituentData',
      label: 'Constituent Data',
      type: 'json',
      required: true,
      placeholder: '{\n  "FirstName": "John",\n  "LastName": "Smith",\n  "EmailAddress": "john@example.com"\n}',
      description: 'Constituent data as JSON object',
      displayOptions: {
        show: {
          resource: ['constituent'],
          action: ['createConstituent', 'updateConstituent'],
        },
      },
    },
    {
      key: 'duplicateCheckData',
      label: 'Person Data',
      type: 'json',
      required: true,
      placeholder: '{\n  "FirstName": "John",\n  "LastName": "Smith",\n  "EmailAddress": "john@example.com",\n  "PostalCode": "AB12 3CD"\n}',
      description: 'Person data to check for duplicates. Available fields: FirstName, LastName, EmailAddress, PhoneNumber, MobilePhone, NationalIdNumber, AddressLine1, PostalCode',
      displayOptions: {
        show: {
          resource: ['constituent'],
          action: ['duplicateCheckPerson'],
        },
      },
    },

    // ===== TRANSACTION FIELDS =====
    {
      key: 'transactionId',
      label: 'Transaction ID',
      type: 'string',
      required: true,
      placeholder: 'e.g., 12345678-1234-1234-1234-123456789012',
      description: 'The GUID of the transaction',
      displayOptions: {
        show: {
          resource: ['transaction'],
          action: ['getTransaction', 'updateTransaction', 'deleteTransaction'],
        },
      },
    },
    {
      key: 'transactionData',
      label: 'Transaction Data',
      type: 'json',
      required: true,
      placeholder: '{\n  "ConstituentId": "...",\n  "Amount": 100.00,\n  "Channel": "Online"\n}',
      description: 'Transaction data as JSON object',
      displayOptions: {
        show: {
          resource: ['transaction'],
          action: ['createTransaction', 'updateTransaction'],
        },
      },
    },

    // ===== ACTIVITY FIELDS =====
    {
      key: 'activityId',
      label: 'Activity ID',
      type: 'string',
      required: true,
      placeholder: 'e.g., 12345678-1234-1234-1234-123456789012',
      description: 'The GUID of the activity',
      displayOptions: {
        show: {
          resource: ['activity'],
          action: ['getActivity', 'updateActivity'],
        },
      },
    },
    {
      key: 'activityData',
      label: 'Activity Data',
      type: 'json',
      required: true,
      placeholder: '{\n  "ConstituentId": "...",\n  "ActivityType": "Email",\n  "Notes": "Sent welcome email"\n}',
      description: 'Activity data as JSON object',
      displayOptions: {
        show: {
          resource: ['activity'],
          action: ['createActivity', 'updateActivity'],
        },
      },
    },

    // ===== LIST FIELDS =====
    {
      key: 'listId',
      label: 'List ID',
      type: 'string',
      required: true,
      placeholder: 'e.g., 12345678-1234-1234-1234-123456789012',
      description: 'The GUID of the saved list',
      displayOptions: {
        show: {
          resource: ['list'],
          action: ['getListResults'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'text',
        loadOptions: {
          method: 'getLists',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'page',
      label: 'Page',
      type: 'number',
      required: false,
      default: 1,
      placeholder: '1',
      description: 'Page number for paginated results',
      displayOptions: {
        show: {
          resource: ['list'],
          action: ['getListResults'],
        },
      },
    },
    {
      key: 'pageSize',
      label: 'Page Size',
      type: 'number',
      required: false,
      default: 100,
      placeholder: '100',
      description: 'Number of results per page (max 1000)',
      displayOptions: {
        show: {
          resource: ['list'],
          action: ['getListResults'],
        },
      },
    },

    // ===== SYSTEM FIELDS =====
    {
      key: 'lookupType',
      label: 'Lookup Type',
      type: 'string',
      required: true,
      placeholder: 'e.g., PaymentMethod',
      description: 'The lookup type to retrieve values for',
      displayOptions: {
        show: {
          resource: ['system'],
          action: ['getLookups'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'text',
        loadOptions: {
          method: 'getLookupTypes',
          cacheResults: true,
          cacheTtl: 120000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
  ],
};

export default schema;
